import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { RoutesMap } from '@Router/router.types.ts';

export const useNonprofitDonorsLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');
  const { t: tValidation } = useTranslation('enJSON');

  const formMethods = useFormHook({
    schemaTitle: 'Nonprofit Donors',
    formDefaultValues: {
      [EddEmbeddedFields.EDD_NONPROFIT_DONORS]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_NONPROFIT_DONORS) || '',
    },
    formSchema: z.object({
      [EddEmbeddedFields.EDD_NONPROFIT_DONORS]: z
        .string()
        .optional()
        .or(z.literal(''))
        .refine(
          (val) => {
            if (!val) return true;
            return val.length <= 300;
          },
          {
            message: tValidation('validation.max300chars'),
          }
        ),
    }),
  });

  const handleSubmit = async () => {
    const donorsValue = formMethods.getValues(EddEmbeddedFields.EDD_NONPROFIT_DONORS);

    if (donorsValue && donorsValue.trim() !== '') {
      saveEddQuestionToLocalStore({
        question: t('organic.nonprofitDonors.question'),
        questionId: EddEmbeddedFields.EDD_NONPROFIT_DONORS,
        answer: JSON.stringify(donorsValue),
      });
    }

    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_NONPROFIT_DONORS);
    } catch (error) {
      console.error(error);
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
  };
};
